import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { attendanceAPI, holidaysAPI, leavesAPI, employeesAPI } from '../services/api';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  UserCheck,
  UserX,
  Clock,
  Calendar,
  User,
  BarChart3,
  Coffee,
  QrCode,
  Search,
  RefreshCw,
  ChevronRight,
  X,
  LogIn,
  LogOut,
} from 'lucide-react';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import { formatNepalTime, getCurrentLocalTime } from '../utils/timeUtils';
import { motion, AnimatePresence } from 'framer-motion';

const EmployeeMobileDashboard = ({
  user,
  record,
  isOnBreak,
  manualPunchAllowed,
  punchLoading,
  breakLoading,
  isRefreshing,
  upcomingHolidays,
  onRefresh,
  onPunch,
  onBreak,
  navigate,
}) => {
  const hasCheckedIn = Boolean(record?.checkInTime);
  const hasCheckedOut = Boolean(record?.checkOutTime);
  const status = hasCheckedOut
    ? 'Shift complete'
    : isOnBreak
      ? 'On break'
      : hasCheckedIn
        ? 'Working'
        : 'Not checked in';
  const statusDot = hasCheckedOut
    ? 'bg-slate-400'
    : isOnBreak
      ? 'bg-amber-400'
      : hasCheckedIn
        ? 'bg-emerald-400'
        : 'bg-slate-400';
  const punchLabel = hasCheckedOut
    ? 'Completed for today'
    : punchLoading
      ? 'Processing…'
      : manualPunchAllowed
        ? hasCheckedIn
          ? 'Punch out'
          : 'Check in'
        : hasCheckedIn
          ? 'Scan to punch out'
          : 'Scan to check in';
  const nextHoliday = upcomingHolidays[0];

  return (
    <div className="space-y-4 md:hidden">
      <header className="flex items-start justify-between gap-3 px-1 pt-1">
        <div className="min-w-0">
          <p className="text-[13px] text-slate-500">
            {format(getCurrentLocalTime(), 'EEEE, MMMM d')}
          </p>
          <h1 className="mt-0.5 truncate text-[22px] font-semibold tracking-[-0.03em] text-slate-950">
            Hello, {user?.name?.split(' ')[0] || 'Employee'}
          </h1>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={isRefreshing}
          aria-label="Refresh dashboard"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 shadow-sm"
        >
          <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
        </button>
      </header>

      <section className="overflow-hidden rounded-[22px] bg-slate-950 text-white shadow-[0_16px_40px_-24px_rgba(15,23,42,0.9)]">
        <div className="px-5 pb-5 pt-4">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-[13px] font-medium text-slate-200">
              <span className={`h-2 w-2 rounded-full ${statusDot}`} />
              {status}
            </div>
            {record?.loginType && (
              <span className="rounded-full border border-white/15 px-2.5 py-1 text-[11px] capitalize text-slate-300">
                {record.loginType}
              </span>
            )}
          </div>

          <div className="mt-6 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
            <div>
              <p className="text-[11px] text-slate-400">Check in</p>
              <p className="mt-1 text-[28px] font-semibold tracking-[-0.04em]">
                {record?.checkInTime ? formatNepalTime(record.checkInTime, 'HH:mm') : '--:--'}
              </p>
            </div>
            <div className="h-10 w-px bg-white/10" />
            <div className="text-right">
              <p className="text-[11px] text-slate-400">Check out</p>
              <p className="mt-1 text-[28px] font-semibold tracking-[-0.04em]">
                {record?.checkOutTime ? formatNepalTime(record.checkOutTime, 'HH:mm') : '--:--'}
              </p>
            </div>
          </div>

          <div className="mt-5 flex items-center justify-between border-t border-white/10 pt-4 text-xs">
            <span className="text-slate-400">Hours today</span>
            <span className="font-medium text-white">
              {typeof record?.workingHours === 'number'
                ? `${record.workingHours.toFixed(1)} hours`
                : hasCheckedIn
                  ? 'In progress'
                  : 'Not started'}
            </span>
          </div>
        </div>

        <div className="bg-white/[0.07] p-3">
          <button
            type="button"
            onClick={onPunch}
            disabled={hasCheckedOut || punchLoading || (hasCheckedIn && isOnBreak)}
            className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-white text-sm font-semibold text-slate-950 transition active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-white/15 disabled:text-slate-400"
          >
            {hasCheckedIn ? <LogOut className="h-4 w-4" /> : <LogIn className="h-4 w-4" />}
            {punchLabel}
          </button>
          <div className="mt-2 grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => navigate('/scan-qr')}
              className="flex h-11 items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 text-[13px] font-medium text-white"
            >
              <QrCode className="h-4 w-4" />
              Scan QR
            </button>
            <button
              type="button"
              onClick={onBreak}
              disabled={!hasCheckedIn || hasCheckedOut || breakLoading}
              className="flex h-11 items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 text-[13px] font-medium text-white disabled:text-slate-600"
            >
              <Coffee className="h-4 w-4" />
              {breakLoading ? 'Please wait…' : isOnBreak ? 'End break' : 'Start break'}
            </button>
          </div>
          {hasCheckedIn && isOnBreak && (
            <p className="mt-2 text-center text-[11px] text-amber-300">
              End your break before punching out.
            </p>
          )}
        </div>
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between px-1">
          <h2 className="text-sm font-semibold text-slate-900">Your workspace</h2>
          <button
            type="button"
            onClick={() => navigate('/profile')}
            className="text-xs font-medium text-slate-500"
          >
            Profile
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          {[
            { label: 'Attendance', detail: 'History & hours', href: '/my-attendance', icon: Clock },
            { label: 'Apply leave', detail: 'Request time off', href: '/leave-application', icon: Calendar },
            { label: 'Leave calendar', detail: 'Team availability', href: '/leave-calendar', icon: Users },
            { label: 'My profile', detail: 'Account details', href: '/profile', icon: User },
          ].map((item) => (
            <button
              key={item.href}
              type="button"
              onClick={() => navigate(item.href)}
              className="min-h-[104px] rounded-2xl border border-slate-200 bg-white p-3.5 text-left shadow-[0_1px_2px_rgba(15,23,42,0.03)] active:bg-slate-50"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-slate-100 text-slate-700">
                <item.icon className="h-4 w-4" />
              </span>
              <span className="mt-3 block text-[13px] font-semibold text-slate-900">{item.label}</span>
              <span className="mt-0.5 block text-[11px] text-slate-500">{item.detail}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white px-4 py-3.5">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
            <Calendar className="h-4 w-4" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] text-slate-500">Next holiday</p>
            <p className="truncate text-sm font-semibold text-slate-900">
              {nextHoliday?.name || 'No upcoming holiday'}
            </p>
          </div>
          {nextHoliday?.date && (
            <div className="shrink-0 text-right">
              <p className="text-sm font-semibold text-slate-900">
                {format(new Date(nextHoliday.date), 'MMM d')}
              </p>
              <p className="text-[10px] text-slate-500">
                {format(new Date(nextHoliday.date), 'EEE')}
              </p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalEmployees: 0,
    present: 0,
    absent: 0,
    late: 0,
    onTime: 0,
    isHoliday: false,
    isWeekend: false,
  });
  const [todayAttendance, setTodayAttendance] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const [isPresentModalOpen, setIsPresentModalOpen] = useState(false);
  const [isAbsentModalOpen, setIsAbsentModalOpen] = useState(false);
  const [isAbsentListOpen, setIsAbsentListOpen] = useState(false);
  const [isLateModalOpen, setIsLateModalOpen] = useState(false);
  const [presentEmployees, setPresentEmployees] = useState([]);
  const [absentEmployees, setAbsentEmployees] = useState([]);
  const [lateEmployees, setLateEmployees] = useState([]);
  const [isPresentLoading, setIsPresentLoading] = useState(false);
  const [isAbsentLoading, setIsAbsentLoading] = useState(false);
  const [isLateLoading, setIsLateLoading] = useState(false);

  const [isOnBreak, setIsOnBreak] = useState(false);
  const [breakLoading, setBreakLoading] = useState(false);
  const [punchLoading, setPunchLoading] = useState(false);
  const [manualPunchAllowed, setManualPunchAllowed] = useState(false);

  const [upcomingHolidays, setUpcomingHolidays] = useState([]);
  const [onLeaveToday, setOnLeaveToday] = useState([]);

  const [trendMode, setTrendMode] = useState('weekly');
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendData, setTrendData] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);

  const fetchUpcomingHolidays = useCallback(async () => {
    try {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const res = await holidaysAPI.getAllHolidays();
      const data = res.data?.data || res.data || [];
      if (!Array.isArray(data)) {
        setUpcomingHolidays([]);
        return;
      }
      setUpcomingHolidays(
        data
          .filter((h) => h.date && new Date(h.date) >= today)
          .sort((a, b) => new Date(a.date) - new Date(b.date))
          .slice(0, 4)
      );
    } catch {
      setUpcomingHolidays([]);
    }
  }, []);

  const fetchLeaveStats = useCallback(async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const next = new Date();
      next.setMonth(next.getMonth() + 1);
      const res = await leavesAPI.getLeaveCalendar(today, next.toISOString().split('T')[0]);
      const leaves = res.data || [];
      setOnLeaveToday(
        leaves.filter(
          (l) => new Date(l.startDate) <= new Date(today) && new Date(l.endDate) >= new Date(today)
        )
      );
    } catch {
      setOnLeaveToday([]);
    }
  }, []);

  const fetchDashboardData = useCallback(
    async (silent = false) => {
      try {
        if (silent) setIsRefreshing(true);
        else setIsLoading(true);

        const todayStr = format(getCurrentLocalTime(), 'yyyy-MM-dd');
        if (user?.role === 'admin') {
          const [statsRes, attRes] = await Promise.all([
            attendanceAPI.getStats(todayStr),
            attendanceAPI.getTodayAttendance(todayStr),
          ]);
          setStats(statsRes.data || {});
          setTodayAttendance(Array.isArray(attRes.data) ? attRes.data : []);
        } else {
          const [attRes, punchAccessRes] = await Promise.all([
            attendanceAPI.getMyAttendance(todayStr, todayStr),
            attendanceAPI.getMobileManualAccess().catch(() => ({ data: { allowed: false } })),
          ]);
          const list = Array.isArray(attRes.data) ? attRes.data : [];
          setTodayAttendance(list);
          setManualPunchAllowed(!!punchAccessRes?.data?.allowed);
          if (list.length > 0) {
            const rec = list[0];
            const ongoing = rec.breaks?.find((b) => b.startTime && !b.endTime);
            setIsOnBreak(Boolean(ongoing));
          } else {
            setIsOnBreak(false);
          }
        }
        fetchUpcomingHolidays();
        fetchLeaveStats();
      } catch {
        toast.error('Failed to load dashboard');
      } finally {
        setIsLoading(false);
        setIsRefreshing(false);
      }
    },
    [user?.role, fetchUpcomingHolidays, fetchLeaveStats]
  );

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const loadPresentEmployees = async () => {
    setIsPresentLoading(true);
    try {
      const res = await attendanceAPI.getPresentEmployees(
        format(getCurrentLocalTime(), 'yyyy-MM-dd')
      );
      setPresentEmployees(res.data?.employees || []);
    } catch {
      setPresentEmployees([]);
    } finally {
      setIsPresentLoading(false);
    }
  };

  const loadAbsentEmployees = async () => {
    setIsAbsentLoading(true);
    try {
      const res = await attendanceAPI.getAbsentEmployees(
        format(getCurrentLocalTime(), 'yyyy-MM-dd')
      );
      setAbsentEmployees(res.data?.employees || []);
    } catch {
      setAbsentEmployees([]);
    } finally {
      setIsAbsentLoading(false);
    }
  };

  const loadLateEmployees = async () => {
    setIsLateLoading(true);
    try {
      const res = await attendanceAPI.getLateEmployees(
        format(getCurrentLocalTime(), 'yyyy-MM-dd')
      );
      setLateEmployees(res.data?.employees || []);
    } catch {
      setLateEmployees([]);
    } finally {
      setIsLateLoading(false);
    }
  };

  const fetchTrendData = useCallback(async () => {
    if (user?.role !== 'admin') {
      setTrendData([]);
      return;
    }

    setTrendLoading(true);
    try {
      const today = new Date();
      const startDate =
        trendMode === 'monthly'
          ? new Date(today.getFullYear(), today.getMonth(), 1)
          : new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6);
      const endDate = today;
      const start = format(startDate, 'yyyy-MM-dd');
      const end = format(endDate, 'yyyy-MM-dd');
      const res = await attendanceAPI.getReport(start, end);
      const records = Array.isArray(res.data) ? res.data : [];

      const days = [];
      for (
        let cursor = new Date(startDate);
        cursor <= endDate;
        cursor.setDate(cursor.getDate() + 1)
      ) {
        days.push(format(new Date(cursor), 'yyyy-MM-dd'));
      }

      const byDate = new Map();
      days.forEach((dateKey) => byDate.set(dateKey, { present: 0, late: 0, total: 0 }));

      records.forEach((record) => {
        const key = format(
          new Date(record.date || record.createdAt || record.checkInTime),
          'yyyy-MM-dd'
        );
        if (!byDate.has(key)) return;
        const bucket = byDate.get(key);
        bucket.total += 1;
        if (['present', 'late', 'half-day'].includes(record.status)) {
          bucket.present += 1;
        }
        if (record.status === 'late') {
          bucket.late += 1;
        }
      });

      const mapped = days.map((dateKey) => {
        const bucket = byDate.get(dateKey) || { present: 0, late: 0, total: 0 };
        const dayLabel = format(
          new Date(`${dateKey}T00:00:00`),
          trendMode === 'monthly' ? 'd' : 'EEE'
        );
        const rate =
          stats.totalEmployees > 0
            ? Math.min(100, Math.round((bucket.present / stats.totalEmployees) * 100))
            : 0;
        return {
          dateKey,
          label: dayLabel,
          rate,
          present: bucket.present,
          late: bucket.late,
          total: bucket.total,
          isToday: format(new Date(dateKey), 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd'),
        };
      });

      setTrendData(mapped);
    } catch {
      setTrendData([]);
    } finally {
      setTrendLoading(false);
    }
  }, [stats.totalEmployees, trendMode, user?.role]);

  const fetchDepartmentData = useCallback(async () => {
    if (user?.role !== 'admin') {
      setDepartmentData([]);
      return;
    }

    try {
      const [employeesRes] = await Promise.all([employeesAPI.getAll()]);
      const employees = Array.isArray(employeesRes.data) ? employeesRes.data : [];
      const presentMap = new Map();
      todayAttendance.forEach((record) => {
        const key =
          record.employeeId?._id || record.employeeId?.employeeId || record.employeeId;
        if (!key) return;
        presentMap.set(String(key), record.status);
      });

      const departmentMap = new Map();
      employees.forEach((employee) => {
        const dept = employee.department || 'Unassigned';
        if (!departmentMap.has(dept)) {
          departmentMap.set(dept, { label: dept, total: 0, present: 0 });
        }
        const row = departmentMap.get(dept);
        row.total += 1;
        const keys = [employee._id, employee.employeeId].filter(Boolean).map(String);
        const isPresent = keys.some((key) =>
          ['present', 'late', 'half-day'].includes(presentMap.get(key))
        );
        if (isPresent) row.present += 1;
      });

      const data = Array.from(departmentMap.values())
        .map((row) => ({
          ...row,
          value: row.total > 0 ? Math.round((row.present / row.total) * 100) : 0,
        }))
        .sort((a, b) => b.total - a.total)
        .slice(0, 5);

      setDepartmentData(data);
    } catch {
      setDepartmentData([]);
    }
  }, [todayAttendance, user?.role]);

  useEffect(() => {
    if (isLoading) return;
    fetchTrendData();
  }, [fetchTrendData, isLoading]);

  useEffect(() => {
    if (isLoading) return;
    fetchDepartmentData();
  }, [fetchDepartmentData, isLoading]);

  const handleBreak = async () => {
    setBreakLoading(true);
    try {
      if (isOnBreak) {
        await attendanceAPI.endBreak(user.employeeId);
        toast.success('Break ended');
        setIsOnBreak(false);
      } else {
        await attendanceAPI.startBreak(user.employeeId);
        toast.success('Break started');
        setIsOnBreak(true);
      }
      fetchDashboardData(true);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally {
      setBreakLoading(false);
    }
  };

  const handlePunch = async () => {
    const employeeRef = String(user?.id || user?._id || user?.employeeId || '').trim();
    if (!employeeRef) {
      toast.error('Employee identifier is missing');
      return;
    }

    const todayRecord = todayAttendance[0] || null;
    const checkedIn = Boolean(todayRecord?.checkInTime);
    const checkedOut = Boolean(todayRecord?.checkOutTime);

    if (checkedOut) {
      toast.error('Attendance already completed for today');
      return;
    }

    if (!manualPunchAllowed) {
      navigate('/scan-qr');
      return;
    }

    if (checkedIn && isOnBreak) {
      toast.error('End your break before punching out');
      return;
    }

    setPunchLoading(true);
    try {
      await attendanceAPI.markAttendance({
        employeeId: employeeRef,
        loginType: 'manual',
      });
      toast.success(checkedIn ? 'Punched out successfully' : 'Punched in successfully');
      fetchDashboardData(true);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Punch failed');
    } finally {
      setPunchLoading(false);
    }
  };

  const statusClass = (status) => {
    if (status === 'present') return 'bg-emerald-50 text-emerald-700';
    if (status === 'late') return 'bg-amber-50 text-amber-800';
    if (status === 'absent') return 'bg-rose-50 text-rose-700';
    if (status === 'half-day') return 'bg-sky-50 text-sky-700';
    return 'bg-gray-100 text-gray-600';
  };

  const filteredLogs = useMemo(() => {
    return todayAttendance.filter((rec) => {
      if (statusFilter !== 'all' && rec.status !== statusFilter) return false;
      if (!searchQuery.trim()) return true;
      const q = searchQuery.toLowerCase();
      const name = (rec.employeeId?.name || user?.name || '').toLowerCase();
      const empId = (rec.employeeId?.employeeId || user?.employeeId || '').toLowerCase();
      const dept = (rec.employeeId?.department || user?.department || '').toLowerCase();
      return name.includes(q) || empId.includes(q) || dept.includes(q);
    });
  }, [todayAttendance, statusFilter, searchQuery, user]);

  if (isLoading) {
    return <div className="py-16 text-center text-sm text-gray-500">Loading dashboard…</div>;
  }

  const totalStaff = Math.max(stats.totalEmployees || 0, 1);
  const attendanceRate = stats.totalEmployees
    ? Math.round(((stats.present || 0) / totalStaff) * 100)
    : 0;
  const absentCount =
    stats.absent ?? Math.max(0, (stats.totalEmployees || 0) - (stats.present || 0));

  const myTodayRecord = todayAttendance[0] || null;

  return (
    <div className="space-y-4">
      {user?.role !== 'admin' && (
        <EmployeeMobileDashboard
          user={user}
          record={myTodayRecord}
          isOnBreak={isOnBreak}
          manualPunchAllowed={manualPunchAllowed}
          punchLoading={punchLoading}
          breakLoading={breakLoading}
          isRefreshing={isRefreshing}
          upcomingHolidays={upcomingHolidays}
          onRefresh={() => fetchDashboardData(true)}
          onPunch={handlePunch}
          onBreak={handleBreak}
          navigate={navigate}
        />
      )}

      <div className={user?.role !== 'admin' ? 'hidden md:block md:space-y-4' : 'space-y-4'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="page-title">
            {user?.role === 'admin' ? 'Dashboard' : `Welcome back, ${user?.name?.split(' ')[0]}`}
          </h1>
          <p className="page-subtitle">
            {format(getCurrentLocalTime(), 'EEEE, MMMM d, yyyy')}
            {stats.isHoliday ? ' · Holiday' : stats.isWeekend ? ' · Weekend' : ''}
          </p>
        </div>

        <div className="flex w-full flex-wrap items-stretch gap-2 sm:w-auto sm:items-center">
          <button
            type="button"
            onClick={() => fetchDashboardData(true)}
            disabled={isRefreshing}
            className="btn-outline flex-1 sm:flex-none"
            title="Refresh"
          >
            <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {user?.role === 'admin' ? (
            <>
              <button
                type="button"
                onClick={() => navigate('/scan-qr')}
                className="btn-outline flex-1 sm:flex-none"
              >
                <QrCode className="w-3.5 h-3.5 mr-1.5" />
                Scan QR
              </button>
              <button
                type="button"
                onClick={() => navigate('/qr-attendance')}
                className="btn-outline flex-1 sm:flex-none"
              >
                QR Station
              </button>
              <button
                type="button"
                onClick={() => navigate('/attendance')}
                className="btn-primary flex-1 sm:flex-none"
              >
                Mark Attendance
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => navigate('/scan-qr')}
                className="btn-outline flex-1 sm:flex-none"
              >
                <QrCode className="w-3.5 h-3.5 mr-1.5" />
                Scan QR
              </button>
              {!Boolean(myTodayRecord?.checkOutTime) && (
                <button
                  type="button"
                  onClick={handlePunch}
                  disabled={punchLoading || (Boolean(myTodayRecord?.checkInTime) && isOnBreak)}
                  className="btn-primary flex-1 sm:flex-none"
                  title={
                    !manualPunchAllowed
                      ? 'Opens Scan QR to punch'
                      : Boolean(myTodayRecord?.checkInTime) && isOnBreak
                        ? 'End break before punching out'
                        : undefined
                  }
                >
                  {Boolean(myTodayRecord?.checkInTime) ? (
                    <LogOut className="w-3.5 h-3.5 mr-1.5" />
                  ) : (
                    <LogIn className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  {punchLoading
                    ? 'Processing…'
                    : Boolean(myTodayRecord?.checkInTime)
                      ? 'Punch out'
                      : 'Check in'}
                </button>
              )}
              <button
                type="button"
                onClick={handleBreak}
                disabled={
                  !myTodayRecord ||
                  Boolean(myTodayRecord.checkOutTime) ||
                  breakLoading ||
                  !myTodayRecord.checkInTime
                }
                className="btn-outline flex-1 sm:flex-none"
              >
                <Coffee className="w-3.5 h-3.5 mr-1.5" />
                {isOnBreak ? 'End break' : 'Start break'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* KPI Cards */}
      {user?.role === 'admin' ? (
        <section className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          <div className="panel !p-4">
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>Total staff</span>
              <Users className="w-3.5 h-3.5 text-gray-400" />
            </div>
            <div className="mt-1.5 text-2xl font-semibold text-gray-900">
              {stats.totalEmployees || 0}
            </div>
            <div className="mt-1 text-xs text-gray-400">Active employees</div>
          </div>

          <button
            type="button"
            onClick={() => {
              setIsPresentModalOpen(true);
              loadPresentEmployees();
            }}
            className="panel !p-4 text-left hover:border-gray-300 transition-colors"
          >
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>Present today</span>
              <UserCheck className="w-3.5 h-3.5 text-gray-400" />
            </div>
            <div className="mt-1.5 flex items-baseline gap-2">
              <span className="text-2xl font-semibold text-gray-900">{stats.present || 0}</span>
              <span className="text-xs font-medium text-emerald-700">{attendanceRate}%</span>
            </div>
            <div className="mt-2 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
              <div
                className="h-full bg-emerald-600 rounded-full"
                style={{ width: `${attendanceRate}%` }}
              />
            </div>
          </button>

          <button
            type="button"
            onClick={() => {
              setIsLateModalOpen(true);
              loadLateEmployees();
            }}
            className="panel !p-4 text-left hover:border-gray-300 transition-colors"
          >
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>Late arrivals</span>
              <Clock className="w-3.5 h-3.5 text-gray-400" />
            </div>
            <div className="mt-1.5 text-2xl font-semibold text-amber-700">{stats.late || 0}</div>
            <div className="mt-1 text-xs text-gray-400">View late list →</div>
          </button>

          <button
            type="button"
            onClick={() => {
              setIsAbsentListOpen(true);
              loadAbsentEmployees();
            }}
            className="panel !p-4 text-left hover:border-gray-300 transition-colors"
          >
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>Absent</span>
              <UserX className="w-3.5 h-3.5 text-gray-400" />
            </div>
            <div className="mt-1.5 text-2xl font-semibold text-rose-700">{absentCount}</div>
            <div className="mt-1 text-xs text-gray-400">View absent list →</div>
          </button>

          <button
            type="button"
            onClick={() => setIsAbsentModalOpen(true)}
            className="panel !p-4 text-left hover:border-gray-300 transition-colors col-span-2 lg:col-span-1"
          >
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>On leave</span>
              <Calendar className="w-3.5 h-3.5 text-gray-400" />
            </div>
            <div className="mt-1.5 text-2xl font-semibold text-gray-900">
              {onLeaveToday.length}
            </div>
            <div className="mt-1 text-xs text-gray-400">Approved today →</div>
          </button>
        </section>
      ) : (
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="panel !p-4">
            <div className="text-xs text-gray-500">Today's status</div>
            <div className="mt-1.5 text-base font-semibold text-gray-900 capitalize">
              {myTodayRecord ? myTodayRecord.status : 'Not checked in'}
            </div>
            <div className="mt-1 text-xs text-gray-400">
              {myTodayRecord?.loginType ? `Source: ${myTodayRecord.loginType}` : 'No record yet'}
            </div>
          </div>
          <div className="panel !p-4">
            <div className="text-xs text-gray-500">Check in / out</div>
            <div className="mt-1.5 text-base font-semibold text-gray-900 font-mono">
              {myTodayRecord?.checkInTime ? formatNepalTime(myTodayRecord.checkInTime, 'HH:mm') : '—'}
              {' / '}
              {myTodayRecord?.checkOutTime
                ? formatNepalTime(myTodayRecord.checkOutTime, 'HH:mm')
                : '—'}
            </div>
            <div className="mt-1 text-xs text-gray-400">
              {myTodayRecord?.workingHours
                ? `${myTodayRecord.workingHours.toFixed(1)} hrs logged`
                : 'Active shift'}
            </div>
          </div>
          <div className="panel !p-4">
            <div className="text-xs text-gray-500">Break status</div>
            <div className="mt-1.5 text-base font-semibold text-gray-900">
              {isOnBreak ? 'On break' : 'Working'}
            </div>
            <div className="mt-1 text-xs text-gray-400">
              {myTodayRecord?.breakTime ? `${myTodayRecord.breakTime} mins total` : 'No breaks'}
            </div>
          </div>
          <div className="panel !p-4">
            <div className="text-xs text-gray-500">Upcoming holidays</div>
            <div className="mt-1.5 text-base font-semibold text-gray-900">
              {upcomingHolidays.length}
            </div>
            <div className="mt-1 text-xs text-gray-400 truncate">
              {upcomingHolidays[0]
                ? `${upcomingHolidays[0].name} (${format(new Date(upcomingHolidays[0].date), 'MMM d')})`
                : 'None scheduled'}
            </div>
          </div>
        </section>
      )}

      {/* Main Content */}
      <section className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_300px] gap-4 items-start">
        <div className="space-y-4 min-w-0">
          <div className="panel !p-0 overflow-hidden">
            <div className="px-3 sm:px-4 py-3 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2.5">
              <div className="min-w-0">
                <h2 className="text-sm font-semibold text-gray-900">
                  Today's attendance ({filteredLogs.length})
                </h2>
                <p className="text-xs text-gray-500">Live check-ins, check-outs, and punch source</p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {user?.role === 'admin' && (
                  <>
                    <div className="relative min-w-0 flex-1 sm:flex-none">
                      <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                      <input
                        type="text"
                        placeholder="Search employee…"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="h-8 w-full pl-8 pr-2.5 text-xs border border-gray-200 rounded-md bg-white text-gray-800 placeholder:text-gray-400 focus:outline-none focus:border-gray-400 sm:w-44"
                      />
                    </div>
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="h-8 px-2 text-xs border border-gray-200 rounded-md bg-white text-gray-700 focus:outline-none focus:border-gray-400"
                    >
                      <option value="all">All status</option>
                      <option value="present">Present</option>
                      <option value="late">Late</option>
                      <option value="half-day">Half-day</option>
                    </select>
                  </>
                )}
                <button
                  type="button"
                  onClick={() => navigate(user?.role === 'admin' ? '/attendance' : '/my-attendance')}
                  className="text-xs font-medium text-gray-600 hover:text-gray-900 whitespace-nowrap"
                >
                  Full log →
                </button>
              </div>
            </div>

            {/* Mobile card list */}
            <div className="divide-y divide-gray-100 sm:hidden">
              {filteredLogs.length > 0 ? (
                filteredLogs.slice(0, 8).map((rec) => {
                  const employeeName = rec.employeeId?.name || user?.name || 'Employee';
                  return (
                    <div key={rec._id} className="px-3 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex min-w-0 items-center gap-2.5">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-900 text-xs font-medium text-white">
                            {employeeName.charAt(0).toUpperCase()}
                          </span>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-medium text-gray-900">{employeeName}</p>
                            <p className="text-xs capitalize text-gray-400">
                              {rec.employeeId?.department || user?.department || '—'}
                            </p>
                          </div>
                        </div>
                        <span className={`inline-flex shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${statusClass(rec.status)}`}>
                          {rec.status}
                        </span>
                      </div>
                      <div className="mt-2.5 grid grid-cols-3 gap-2 text-center">
                        <div className="rounded-md bg-gray-50 px-2 py-1.5">
                          <p className="font-mono text-[13px] text-gray-900">
                            {rec.checkInTime ? formatNepalTime(rec.checkInTime, 'HH:mm') : '—'}
                          </p>
                          <p className="text-[10px] uppercase text-gray-400">In</p>
                        </div>
                        <div className="rounded-md bg-gray-50 px-2 py-1.5">
                          <p className="font-mono text-[13px] text-gray-900">
                            {rec.checkOutTime ? formatNepalTime(rec.checkOutTime, 'HH:mm') : '—'}
                          </p>
                          <p className="text-[10px] uppercase text-gray-400">Out</p>
                        </div>
                        <div className="rounded-md bg-gray-50 px-2 py-1.5">
                          <p className="font-mono text-[13px] text-gray-900">
                            {rec.workingHours ? `${Number(rec.workingHours).toFixed(1)}h` : '—'}
                          </p>
                          <p className="text-[10px] uppercase text-gray-400">Hours</p>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <p className="px-3 py-10 text-center text-xs text-gray-400">
                  No matching attendance records for today.
                </p>
              )}
            </div>

            {/* Desktop table */}
            <div className="table-scroll hidden sm:block">
              <table className="table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Department</th>
                    <th>Check in</th>
                    <th>Check out</th>
                    <th>Hours</th>
                    <th>Source</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLogs.length > 0 ? (
                    filteredLogs.slice(0, 8).map((rec) => {
                      const employeeName = rec.employeeId?.name || user?.name || 'Employee';
                      const empCode = rec.employeeId?.employeeId || user?.employeeId || '—';
                      return (
                        <tr key={rec._id}>
                          <td>
                            <div className="flex items-center gap-3">
                              <span className="h-8 w-8 rounded-full bg-gray-900 text-white text-xs font-medium flex items-center justify-center shrink-0">
                                {employeeName.charAt(0).toUpperCase()}
                              </span>
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900">{employeeName}</div>
                                <div className="text-xs text-gray-400">{empCode}</div>
                              </div>
                            </div>
                          </td>
                          <td className="capitalize">{rec.employeeId?.department || user?.department || '—'}</td>
                          <td className="font-mono text-[13px] text-gray-900">
                            {rec.checkInTime ? formatNepalTime(rec.checkInTime, 'HH:mm') : '—'}
                          </td>
                          <td className="font-mono text-[13px] text-gray-900">
                            {rec.checkOutTime ? formatNepalTime(rec.checkOutTime, 'HH:mm') : '—'}
                          </td>
                          <td className="font-mono text-[13px] text-gray-700">
                            {rec.workingHours ? `${Number(rec.workingHours).toFixed(1)}h` : '—'}
                          </td>
                          <td className="text-xs uppercase tracking-wide text-gray-500">
                            {rec.loginType || 'manual'}
                          </td>
                          <td>
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${statusClass(rec.status)}`}>
                              {rec.status}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" className="py-10 text-center text-xs text-gray-400">
                        No matching attendance records for today.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Attendance Trend Chart (Admin) */}
          {user?.role === 'admin' && (
            <div className="panel !p-0 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between gap-4">
                <div>
                  <h2 className="text-sm font-semibold text-gray-900">Attendance trend</h2>
                  <p className="text-xs text-gray-500">Daily check-in volume</p>
                </div>
                <div className="inline-flex rounded-md border border-gray-200 p-0.5 text-xs">
                  <button
                    type="button"
                    onClick={() => setTrendMode('weekly')}
                    className={`rounded px-2.5 py-1 transition-colors ${
                      trendMode === 'weekly'
                        ? 'bg-gray-900 text-white font-medium'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    7 days
                  </button>
                  <button
                    type="button"
                    onClick={() => setTrendMode('monthly')}
                    className={`rounded px-2.5 py-1 transition-colors ${
                      trendMode === 'monthly'
                        ? 'bg-gray-900 text-white font-medium'
                        : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    Month
                  </button>
                </div>
              </div>

              <div className="p-4">
                <div className="h-[170px] rounded-md bg-gray-50 border border-gray-100 px-3 pt-5 pb-2.5 flex items-end justify-between gap-1.5 overflow-x-auto">
                  {trendLoading ? (
                    <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
                      Loading trend…
                    </div>
                  ) : trendData.length > 0 ? (
                    trendData.map((point) => (
                      <div
                        key={point.dateKey}
                        className="flex flex-col items-center gap-1 flex-1 min-w-[18px] h-full justify-end"
                        title={`${point.dateKey}: ${point.present} present (${point.rate}%)`}
                      >
                        <div className="text-[10px] text-gray-500">{point.present}</div>
                        <div className="w-full max-w-[24px] h-[100px] flex items-end">
                          <div
                            className={`w-full rounded-t ${
                              point.isToday ? 'bg-gray-900' : 'bg-gray-300'
                            }`}
                            style={{ height: `${Math.max(point.rate, 6)}%` }}
                          />
                        </div>
                        <div
                          className={`text-[10px] truncate ${
                            point.isToday ? 'text-gray-900 font-semibold' : 'text-gray-400'
                          }`}
                        >
                          {point.label}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
                      No trend data available
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Column */}
        <div className="space-y-4">
          {/* Quick Links */}
          <div className="panel !p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200">
              <h2 className="text-sm font-semibold text-gray-900">Shortcuts</h2>
            </div>
            <div className="divide-y divide-gray-100">
              {(user?.role === 'admin'
                ? [
                    { label: 'QR Attendance Station', sub: 'Display rotating check-in QR', href: '/qr-attendance', icon: QrCode },
                    { label: 'Employee Directory', sub: 'Add or edit staff members', href: '/employees', icon: Users },
                    { label: 'Leave Approvals', sub: 'Review pending requests', href: '/leave-approval', icon: Calendar },
                    { label: 'Monthly Reports', sub: 'Attendance summary & export', href: '/reports', icon: BarChart3 },
                  ]
                : [
                    { label: 'My Attendance History', sub: 'View monthly logs & hours', href: '/my-attendance', icon: Calendar },
                    { label: 'Apply for Leave', sub: 'Submit time-off request', href: '/leave-application', icon: Clock },
                    { label: 'Time-Off Calendar', sub: 'See team leaves & holidays', href: '/leave-calendar', icon: Users },
                    { label: 'Account Profile', sub: 'Personal details & password', href: '/profile', icon: User },
                  ]
              ).map((item) => (
                <button
                  key={item.href}
                  type="button"
                  onClick={() => navigate(item.href)}
                  className="w-full px-4 py-2.5 flex items-center justify-between gap-3 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center shrink-0">
                      <item.icon className="w-3.5 h-3.5 text-gray-600" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-gray-900 truncate">{item.label}</div>
                      <div className="text-[11px] text-gray-400 truncate">{item.sub}</div>
                    </div>
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                </button>
              ))}
            </div>
          </div>

          {/* Department Attendance (Admin) */}
          {user?.role === 'admin' && (
            <div className="panel !p-0 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">Department coverage</h2>
                <span className="text-xs text-gray-400">Today</span>
              </div>
              <div className="p-4 space-y-3">
                {departmentData.length > 0 ? (
                  departmentData.map((row) => (
                    <div key={row.label}>
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium text-gray-800">{row.label}</span>
                        <span className="text-gray-500 font-mono">
                          {row.present}/{row.total} ({row.value}%)
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gray-800"
                          style={{ width: `${row.value}%` }}
                        />
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-xs text-gray-400">No department data available.</div>
                )}
              </div>
            </div>
          )}

          {/* On Leave & Upcoming Holidays */}
          <div className="panel !p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-gray-900">Leaves & holidays</h2>
              <button
                type="button"
                onClick={() => navigate('/leave-calendar')}
                className="text-xs text-gray-500 hover:text-gray-900"
              >
                Calendar →
              </button>
            </div>
            <div className="p-4 space-y-3 text-xs">
              <div>
                <div className="text-[11px] font-medium text-gray-400 uppercase mb-1.5">
                  On leave today ({onLeaveToday.length})
                </div>
                {onLeaveToday.length > 0 ? (
                  <div className="space-y-1.5">
                    {onLeaveToday.slice(0, 3).map((leave) => (
                      <div
                        key={leave._id}
                        className="flex items-center justify-between py-1 border-b border-gray-100 last:border-0"
                      >
                        <span className="font-medium text-gray-800 truncate">
                          {leave.employeeId?.name || 'Employee'}
                        </span>
                        <span className="text-gray-500 shrink-0">
                          {leave.leaveTypeId?.name || 'Leave'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-400">Everyone is scheduled in today.</div>
                )}
              </div>

              <div className="pt-2 border-t border-gray-100">
                <div className="text-[11px] font-medium text-gray-400 uppercase mb-1.5">
                  Upcoming holidays ({upcomingHolidays.length})
                </div>
                {upcomingHolidays.length > 0 ? (
                  <div className="space-y-1.5">
                    {upcomingHolidays.map((h) => (
                      <div
                        key={h._id || h.date}
                        className="flex items-center justify-between py-1 border-b border-gray-100 last:border-0"
                      >
                        <span className="font-medium text-gray-800 truncate">{h.name}</span>
                        <span className="text-gray-500 font-mono shrink-0">
                          {format(new Date(h.date), 'MMM d')}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-gray-400">No upcoming holidays scheduled.</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {isPresentModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/30"
          >
            <div className="bg-white w-full max-w-md rounded-t-xl sm:rounded-lg border border-gray-200 shadow-lg overflow-hidden max-h-[85vh] sm:max-h-[70vh] flex flex-col">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">
                  Present today ({presentEmployees.length})
                </h2>
                <button
                  type="button"
                  onClick={() => setIsPresentModalOpen(false)}
                  className="p-1 text-gray-400 hover:text-gray-700 rounded"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="p-4 overflow-y-auto space-y-1.5">
                {isPresentLoading ? (
                  <div className="text-center py-6 text-xs text-gray-400">Loading…</div>
                ) : presentEmployees.length ? (
                  presentEmployees.map((e) => (
                    <div
                      key={e._id || e.employeeId}
                      className="flex items-center justify-between p-2 rounded border border-gray-200 text-xs"
                    >
                      <div>
                        <div className="font-medium text-gray-900">{e.name}</div>
                        <div className="text-gray-500">{e.department || '—'}</div>
                      </div>
                      {e.checkInTime && (
                        <span className="font-mono text-gray-600">
                          {formatNepalTime(e.checkInTime, 'HH:mm')}
                        </span>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-center text-xs text-gray-400 py-4">No one checked in yet</p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isAbsentModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/30"
          >
            <div className="bg-white w-full max-w-md rounded-t-xl sm:rounded-lg border border-gray-200 shadow-lg overflow-hidden max-h-[85vh] sm:max-h-[70vh] flex flex-col">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">
                  On leave today ({onLeaveToday.length})
                </h2>
                <button
                  type="button"
                  onClick={() => setIsAbsentModalOpen(false)}
                  className="p-1 text-gray-400 hover:text-gray-700 rounded"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="p-4 overflow-y-auto space-y-2">
                {onLeaveToday.length > 0 ? (
                  onLeaveToday.map((leave) => (
                    <div key={leave._id} className="rounded border border-gray-200 p-2.5 text-xs">
                      <div className="font-medium text-gray-900">{leave.employeeId?.name}</div>
                      <div className="text-gray-500 mt-0.5">
                        {leave.leaveTypeId?.name} · {format(new Date(leave.startDate), 'MMM d')} –{' '}
                        {format(new Date(leave.endDate), 'MMM d')}
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-xs text-gray-400 py-4">No one is on leave today</p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isLateModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/30"
          >
            <div className="bg-white w-full max-w-md rounded-t-xl sm:rounded-lg border border-gray-200 shadow-lg overflow-hidden max-h-[85vh] sm:max-h-[70vh] flex flex-col">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">
                  Late arrivals ({lateEmployees.length})
                </h2>
                <button
                  type="button"
                  onClick={() => setIsLateModalOpen(false)}
                  className="p-1 text-gray-400 hover:text-gray-700 rounded"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="p-4 overflow-y-auto space-y-1.5">
                {isLateLoading ? (
                  <div className="text-center py-6 text-xs text-gray-400">Loading…</div>
                ) : lateEmployees.length ? (
                  lateEmployees.map((e) => (
                    <div
                      key={e._id || e.employeeId}
                      className="flex items-center justify-between p-2 rounded border border-gray-200 text-xs"
                    >
                      <div>
                        <div className="font-medium text-gray-900">{e.name}</div>
                        <div className="text-gray-500">{e.department || '—'}</div>
                      </div>
                      {e.checkInTime && (
                        <span className="font-mono text-gray-600">
                          {formatNepalTime(e.checkInTime, 'HH:mm')}
                        </span>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-center text-xs text-gray-400 py-4">No late arrivals today</p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isAbsentListOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/30"
          >
            <div className="bg-white w-full max-w-md rounded-t-xl sm:rounded-lg border border-gray-200 shadow-lg overflow-hidden max-h-[85vh] sm:max-h-[70vh] flex flex-col">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-sm font-semibold text-gray-900">
                  Absent today ({absentEmployees.length})
                </h2>
                <button
                  type="button"
                  onClick={() => setIsAbsentListOpen(false)}
                  className="p-1 text-gray-400 hover:text-gray-700 rounded"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="p-4 overflow-y-auto space-y-1.5">
                {isAbsentLoading ? (
                  <div className="text-center py-6 text-xs text-gray-400">Loading…</div>
                ) : absentEmployees.length ? (
                  absentEmployees.map((e) => (
                    <div
                      key={e._id || e.employeeId}
                      className="flex items-center justify-between p-2 rounded border border-gray-200 text-xs"
                    >
                      <div>
                        <div className="font-medium text-gray-900">{e.name}</div>
                        <div className="text-gray-500">
                          {e.department || '—'}
                          {e.employeeId ? ` · ${e.employeeId}` : ''}
                        </div>
                      </div>
                      <span className="text-[11px] font-medium text-rose-700 bg-rose-50 rounded px-1.5 py-0.5">
                        Absent
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-center text-xs text-gray-400 py-4">No one is absent today</p>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;
